import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IState } from '..';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import CalendarIconUrl from '../../../../assets/icon_calendar.svg';
import { DayPicker } from '../../../../components/DayPicker';
import { ToggleAnimationWrapper } from '../../../../components/helpers/ToggleAnimationWrapper';
import { Button } from '../../../../components-v2/Button';
import { Input } from '../../../../components-v2/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { TagOnCompletion } from '../../shared/components/TagOnCompletion';
import { styleSheet } from '../styles';
import { ConfirmationEmailSender } from './ConfirmationEmailSender';
import { GuestEmailModeField } from './GuestEmailModeField';
import { EventRegistrationFormData } from '../utils';

interface AttendeesSettingsProps {
	state: {
		enableRequestAttendeesToRSVP: boolean;
		now: Date;
		selectedImage: Api.IFileAttachment | null;
	};
	eventData?: Api.IEventRegistrationSurvey;
	account?: Api.IAccount;
	publishableApiKeys?: Api.IStripeKeys;
	onToggleCheckChanged: () => void;
	onClickMedia: () => void;
	onClickFreeImage: () => void;
	isClone?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
}

export const AttendeesSettings: React.FC<AttendeesSettingsProps> = ({
	eventData,
	account,
	isClone,
	impersonationContext,
}) => {
	const { control, watch, setValue, formState } = useFormContext<EventRegistrationFormData>();
	const formValues = watch();

	const [allowGuests, setAllowGuests] = React.useState<boolean>(Number(formValues.guestLimit) > 0);
	// Snapshots guestLimit at toggle-off time so toggling back on restores it. Not updated on every input change.
	const [prevGuestLimit, setPrevGuestLimit] = React.useState<number>(
		Number(formValues.guestLimit) > 0 ? Number(formValues.guestLimit) : 1
	);

	// When async initialization loads an existing event with guestLimit set, open the section.
	// Only fires when guestLimit becomes > 0 so clearing the input doesn't collapse the section.
	React.useEffect(() => {
		if (Number(formValues.guestLimit) > 0) {
			setAllowGuests(true);
		}
	}, [formValues.guestLimit]);

	const handleAllowGuestsToggle = () => {
		if (allowGuests) {
			const current = Number(formValues.guestLimit);
			setPrevGuestLimit(current > 0 ? current : prevGuestLimit);
			setValue('guestLimit', 0);
			setAllowGuests(false);
		} else {
			setValue('guestLimit', prevGuestLimit);
			setAllowGuests(true);
		}
	};

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Attendees</h3>
			<div>
				<TagOnCompletion />
			</div>

			<FormFieldWrapper
				name='enableRequestAttendeesToRSVP'
				control={control}
				title='Enable Attendees RSVP Options'
				description='Allow attendees to RSVP with additional options'
			>
				{({ value, onChange }) => (
					<Toggle
						id='request-attendees-rsvp-toggle'
						isOn={value}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>

			<ToggleAnimationWrapper isVisible={formValues.enableRequestAttendeesToRSVP}>
				<fieldset disabled={!formValues.enableRequestAttendeesToRSVP} className={css(styleSheet.fieldset)}>
					<FormFieldWrapper
						name='requirePhoneNumber'
						control={control}
						title='Require Phone Number'
						description='Require attendees to provide phone number'
					>
						{({ value, onChange }) => (
							<Toggle
								id='require-attendees-phone-number-checkbox'
								isOn={value}
								onToggleCheckChanged={() => {
									if (!formValues.enableRequestAttendeesToRSVP) {
										return;
									}
									onChange(!value);
								}}
								uncheckedColor={grayIconFill}
							/>
						)}
					</FormFieldWrapper>
					<FormFieldWrapper
						name='requireDeadline'
						control={control}
						title='RSVP Deadline'
						description='Please RSVP by… (deadline)'
					>
						{({ value, onChange }) => (
							<Toggle
								id='rsvp-by-deadline-checkbox'
								isOn={value}
								onToggleCheckChanged={() => {
									if (!formValues.enableRequestAttendeesToRSVP) {
										return;
									}
									if (!value) {
										setValue('registrationDeadline', null);
									}
									onChange(!value);
								}}
								uncheckedColor={grayIconFill}
							/>
						)}
					</FormFieldWrapper>
					<div className={css(styleSheet.formControlWrap)}>
						<ToggleAnimationWrapper isVisible={formValues.requireDeadline}>
							<div className={css(styleSheet.deadlineDateInput)}>
								<Controller
									name='registrationDeadline'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => {
										return (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<span className={css(styleSheet.eventDateInput, styleSheet.eventDateInputSmall)}>
															<img src={CalendarIconUrl} className={css(styleSheet.calendarIcon)} alt='Calendar icon' />
															<span className={css(styleSheet.selectedDate)}>
																{fieldProps.value && getDefaultDateStringValue(fieldProps.value)}
															</span>
														</span>
													</PopoverTrigger>
													<PopoverContent>
														<div style={{ padding: '20px 10px' }}>
															<DayPicker
																allowPastDates={false}
																minDate={new Date()}
																maxDate={new Date(formValues.endDate)}
																onDayClick={(val: Date) => {
																	fieldProps.onChange(val);
																}}
																selectedDays={
																	fieldProps.value
																		? new Date(fieldProps.value)
																		: formValues.startDate
																			? new Date(formValues.startDate)
																			: new Date()
																}
															/>
														</div>
													</PopoverContent>
												</Popover>

												{fieldProps.value && (
													<Button
														type='button'
														variant='link'
														styles={[styleSheet.clearDeadline]}
														onClick={(e: React.MouseEvent) => {
															e.preventDefault();
															fieldProps.onChange(null);
														}}
													>
														Clear
													</Button>
												)}
											</>
										);
									}}
								/>
								{formState.isSubmitted && formState.errors.registrationDeadline && (
									<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
										{formState.errors.registrationDeadline.message}
									</p>
								)}
							</div>
						</ToggleAnimationWrapper>
					</div>

					{!formValues.isPaidEvent && (
						<>
							{/* Allow Guests toggle row */}
							<div className={css(styleSheet.allowGuestsRow)}>
								<div className={css(styleSheet.allowGuestsText)}>
									<span className={css(styleSheet.allowGuestsLabel)}>Allow Guests</span>
									<p className={css(styleSheet.allowGuestsDescription)}>Let attendees bring additional guests</p>
								</div>
								<Toggle
									id='allow-guests-checkbox'
									isOn={allowGuests}
									onToggleCheckChanged={handleAllowGuestsToggle}
									uncheckedColor={grayIconFill}
								/>
							</div>

							<ToggleAnimationWrapper isVisible={allowGuests}>
								<div className={css(styleSheet.guestSubSection)}>
									<FormFieldWrapper
										name='requireGuestNames'
										control={control}
										title='Require Guest Names'
										description='Guests must provide their names'
									>
										{({ value, onChange }) => (
											<Toggle
												id='require-guest-names-checkbox'
												isOn={value}
												onToggleCheckChanged={() => {
													if (!formValues.enableRequestAttendeesToRSVP) {
														return;
													}
													onChange(!value);
												}}
												uncheckedColor={grayIconFill}
											/>
										)}
									</FormFieldWrapper>

									<GuestEmailModeField />

									<FormFieldWrapper
										name='guestLimit'
										control={control}
										title='Guests per attendee'
										description='Max number of guests each attendee may bring'
										showError={false}
									>
										{({ value, onChange, ref }) => (
											<Input
												id='guest-limit-input'
												ref={ref}
												min={1}
												type='number'
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
												value={value}
											/>
										)}
									</FormFieldWrapper>
									{formState.isSubmitted && formState.errors.guestLimit && (
										<p className={css(styleSheet.guestLimitError)}>{formState.errors.guestLimit.message}</p>
									)}
									{Number(formValues.guestLimit) > 0 && (
										<p className={css(styleSheet.guestLimitHelperText)} aria-live='polite'>
											{`Up to ${Number(formValues.guestLimit)} guests per attendee`}
										</p>
									)}
								</div>
							</ToggleAnimationWrapper>

							<FormFieldWrapper
								name='requireMaximumCapacity'
								control={control}
								title='Set Maximum Capacity'
								description='Set maximum capacity'
							>
								{({ value, onChange }) => (
									<Toggle
										id='set-maximum-capacity-checkbox'
										isOn={value}
										onToggleCheckChanged={() => {
											if (!formValues.enableRequestAttendeesToRSVP) {
												return;
											}
											const toggle = !value;
											if (toggle !== true) {
												setValue('maximumCapacity', formState.defaultValues?.maximumCapacity);
											}
											onChange(toggle);
										}}
										uncheckedColor={grayIconFill}
									/>
								)}
							</FormFieldWrapper>
							<div className={css(styleSheet.formControlWrap)}>
								<ToggleAnimationWrapper isVisible={formValues.requireMaximumCapacity}>
									<div className={css(styleSheet.maximumCapasityInput)}>
										<Controller
											name='maximumCapacity'
											control={control}
											render={({ field: { ref, ...fieldProps } }) => (
												<Input
													id='maximum-capacity-input'
													ref={ref}
													min={1}
													type='number'
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														fieldProps.onChange(e.target.value);
													}}
													value={fieldProps.value}
												/>
											)}
										/>
										{formState.isSubmitted && formState.errors.maximumCapacity && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{formState.errors.maximumCapacity.message}
											</p>
										)}
									</div>
								</ToggleAnimationWrapper>
							</div>
						</>
					)}
					{formValues.isPaidEvent && (
						<>
							<FormFieldWrapper
								name='requireGuestNames'
								control={control}
								title='Require Guest Names'
								description='Guests must provide their names'
							>
								{({ value, onChange }) => (
									<Toggle
										id='require-guest-names-checkbox'
										isOn={value}
										onToggleCheckChanged={() => {
											if (!formValues.enableRequestAttendeesToRSVP) {
												return;
											}
											onChange(!value);
										}}
										uncheckedColor={grayIconFill}
									/>
								)}
							</FormFieldWrapper>
							<GuestEmailModeField />
							<p className={css(styleSheet.paidEventGuestNote)}>
								Guests are always allowed for paid events. Capacity is controlled by ticket inventory.
							</p>
						</>
					)}
				</fieldset>
			</ToggleAnimationWrapper>

			<FormFieldWrapper
				name='includeCheckInQrCode'
				control={control}
				title='Include Check-In QR Code'
				description='Include a check-in QR code and link in the registration confirmation email'
			>
				{({ value, onChange }) => (
					<Toggle
						id='include-check-in-qr-code-toggle'
						isOn={value}
						onToggleCheckChanged={() => onChange(!value)}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			<ConfirmationEmailSender
				eventData={eventData}
				account={account}
				isClone={isClone}
				impersonationContext={impersonationContext}
			/>
		</section>
	);
};
